import { NOTIFICATIONS_FETCH_URL, NOTIFICATIONS_URL } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useQuery from "@/hooks/useQuery";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import { IoCheckmarkDone, IoDocumentText, IoCarSport } from "react-icons/io5";

interface ApiNotification {
  id: string;
  event_type:
    | "order.created"
    | "invoice.generated"
    | "order.shipped"
    | "order.accepted"
    | string;
  title: string;
  body: string;
  metadata: {
    total_amount?: string;
    amount_due?: string;
    order_id?: string;
    invoice_id?: string;
    estimated_delivery_range?: string;
  };
  created_at: string;
  read_at: string | null;
}

interface Notification {
  id: string;
  type: "order" | "invoice" | "shipped";
  title: string;
  description: string;
  timestamp: string;
  isRead: boolean;
  details?: string;
  invoiceId?: string;
}

// Map API event_type → internal type
const eventTypeMap: Record<string, Notification["type"]> = {
  "order.created": "order",
  "invoice.generated": "invoice",
  "order.shipped": "shipped",
  "order.accepted": "order",
};

const notificationIcons: Record<string, React.ReactNode> = {
  order: <IoCheckmarkDone className="w-full h-full" />,
  invoice: <IoDocumentText className="w-full h-full" />,
  shipped: <IoCarSport className="w-full h-full" />,
};

const notificationColors: Record<string, string> = {
  order: "bg-green-100 text-green-600",
  invoice: "bg-blue-100 text-blue-600",
  shipped: "bg-yellow-100 text-yellow-600",
};

// Format API date → relative time string
function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Format amount for display
function formatAmount(
  metadata: ApiNotification["metadata"],
): string | undefined {
  const raw = metadata?.total_amount || metadata?.amount_due;
  if (!raw) return undefined;
  return `₦${Number(raw).toLocaleString()}`;
}

// Transform API response → internal Notification shape
function transformNotification(n: ApiNotification): Notification {
  const type = eventTypeMap[n.event_type] ?? "order";
  const amount = formatAmount(n.metadata);

  return {
    id: n.id,
    type,
    title: n.title,
    description: n.body,
    timestamp: relativeTime(n.created_at),
    isRead: !!n.read_at,
    details: amount,
    invoiceId: n.metadata?.invoice_id,
  };
}

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "unread" | "read">(
    "all",
  );
  const [, setSelectedNotification] = useState<Notification | null>(null);
  const navigate = useNavigate();

  const { data: notificationsData } = useQuery(NOTIFICATIONS_FETCH_URL);
  const { data: unRead } = useQuery(NOTIFICATIONS_URL);

  // Populate notifications from API data
  useEffect(() => {
    if (notificationsData && Array.isArray(notificationsData)) {
      setNotifications(
        (notificationsData as ApiNotification[]).map(transformNotification),
      );
    }
  }, [notificationsData]);

  const filteredNotifications = notifications.filter((notif) => {
    const matchesSearch =
      notif.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notif.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterType === "all" ||
      (filterType === "unread" && !notif.isRead) ||
      (filterType === "read" && notif.isRead);

    return matchesSearch && matchesFilter;
  });

  const handleMarkAsRead = (id: string) => {
    setNotifications(
      notifications.map((notif) =>
        notif.id === id ? { ...notif, isRead: true } : notif,
      ),
    );
  };

  const handleDismiss = (id: string) => {
    setNotifications(notifications.filter((notif) => notif.id !== id));
  };

  const apiCall = useApiCallHandling();

  const handleMarkAllAsRead = async () => {
    await apiCall(
      `/api/v1/notifications/read-all`,
      "POST",
      {},
      () =>
        setNotifications(notifications.map((n) => ({ ...n, isRead: true }))),
      () => {},
      () => {},
    );
  };

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg overflow-hidden px-5">
      {/* Header */}
      <div className="py-6 border-b border-gray-200">
        <div className="flex items-center justify-between ">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-500 mt-1">
              {unRead?.unread_count ??
                notifications.filter((n) => !n.isRead).length}{" "}
              unread notifications
            </p>
          </div>
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 text-sm font-medium text-blue-800 hover:text-blue-600 transition"
          >
            Mark all as read
          </button>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
            />
          </div>
          <div className="flex gap-2">
            {(["all", "unread", "read"] as const).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  filterType === type
                    ? "bg-blue-800 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="divide-y divide-gray-200 max-h-[800px] overflow-y-auto">
        {filteredNotifications.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No notifications found</p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => {
                setSelectedNotification(notification);
                handleMarkAsRead(notification.id);
                // MED-702: route straight to the order's invoice when the
                // notification carries one (omitted server-side for Cash on
                // Delivery, where nothing's owed yet).
                if (notification.invoiceId) {
                  navigate(`/dashboard/invoices/${notification.invoiceId}?from=medex`);
                }
              }}
              className={`p-4 cursor-pointer transition group hover:bg-gray-50 ${
                !notification.isRead ? "bg-red-50" : ""
              }`}
            >
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm ${
                      notificationColors[notification.type]
                    }`}
                  >
                    {notificationIcons[notification.type]}
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1">
                      <p
                        className={`text-sm ${
                          notification.isRead
                            ? "font-normal text-gray-600"
                            : "font-semibold text-gray-900"
                        }`}
                      >
                        {notification.title}
                      </p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {notification.description}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {notification.timestamp}
                    </span>
                  </div>

                  {notification.details && (
                    <p className="text-xs text-gray-500 mt-2">
                      {notification.details}
                    </p>
                  )}
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDismiss(notification.id);
                  }}
                  className="text-gray-400 hover:text-red-600 transition flex-shrink-0 opacity-0 group-hover:opacity-100"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
